import { useCallback, useEffect, useRef, useState } from 'react';
import { isLocal, subscribe, type BusMessage } from './bus';

export interface QueryState<T> {
  data: T | undefined;
  loading: boolean;
  error: Error | null;
  reload: () => void;
}

/**
 * Run an async read and re-run it whenever one of the listed tables changes
 * (in this tab or another). Keeps the previous data while refreshing so the
 * screen never flashes empty (section 24.2).
 */
export function useQuery<T>(fn: () => Promise<T>, deps: unknown[], tables: string[] = []): QueryState<T> {
  const [data, setData] = useState<T | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [tick, setTick] = useState(0);
  const alive = useRef(true);
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    alive.current = true;
    let cancelled = false;
    setLoading(true);
    fnRef
      .current()
      .then((v) => {
        if (!cancelled && alive.current) {
          setData(v);
          setError(null);
          setLoading(false);
        }
      })
      .catch((e: unknown) => {
        if (!cancelled && alive.current) {
          setError(e instanceof Error ? e : new Error(String(e)));
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);

  useEffect(() => {
    if (!tables.length) return;
    return subscribe((m: BusMessage) => {
      // Remote messages are absorbed by the store first, which then re-emits locally.
      if (!isLocal(m)) return;
      if (m.type === 'reset') setTick((t) => t + 1);
      if (m.type === 'changed' && (m.tables.includes('*') || m.tables.some((t) => tables.includes(t)))) setTick((t) => t + 1);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tables.join('|')]);

  useEffect(() => () => { alive.current = false; }, []);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { data, loading, error, reload };
}

export function useMediaQuery(query: string): boolean {
  const get = () => (typeof window !== 'undefined' && 'matchMedia' in window ? window.matchMedia(query).matches : false);
  const [matches, setMatches] = useState(get);
  useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = () => setMatches(mql.matches);
    handler();
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [query]);
  return matches;
}

export const useIsDesktop = (): boolean => useMediaQuery('(min-width: 900px)');

export function useBrowserOnline(): boolean {
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
}

/** Small local-storage backed state with JSON serialisation and try/catch. */
export function useLocalState<T>(key: string, initial: T): [T, (v: T | ((prev: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw ? (JSON.parse(raw) as T) : initial;
    } catch {
      return initial;
    }
  });
  const set = useCallback(
    (v: T | ((prev: T) => T)) => {
      setValue((prev) => {
        const next = typeof v === 'function' ? (v as (p: T) => T)(prev) : v;
        try {
          localStorage.setItem(key, JSON.stringify(next));
        } catch {
          /* ignore */
        }
        return next;
      });
    },
    [key],
  );
  return [value, set];
}

export function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/**
 * A long list, a screenful at a time.
 *
 * Returns how many rows to draw and a ref for a marker placed after the last of
 * them; when the marker comes near the screen another `step` rows are added.
 * The list only ever grows, so what you have scrolled past stays where it was
 * and scrolling back up never jumps.
 *
 * The family's line is a row for every story, every birth and every simcha — a
 * few dozen in the demo, six or seven thousand for a real family a few years in
 * — and drawing all of them to show the first ten is a hundred thousand
 * elements built before anything appears on a phone. `resetKey` is whatever
 * decides *which* rows there are (the filters, the order): when it changes the
 * list starts again from its first screenful.
 */
export function useGrowingList(total: number, step: number, resetKey: unknown): [number, (el: Element | null) => void] {
  const [limit, setLimit] = useState(step);
  const [marker, setMarker] = useState<Element | null>(null);
  useEffect(() => setLimit(step), [resetKey, step]);
  useEffect(() => {
    if (!marker || limit >= total) return undefined;
    // No IntersectionObserver (an old browser, a test runner): show everything rather than a list that cannot grow.
    if (typeof IntersectionObserver === 'undefined') {
      setLimit(total);
      return undefined;
    }
    /*
     * Made afresh each time the list grows, on purpose: an observer only reports
     * a *change*, and a marker that is still near the screen after the new rows
     * arrived (short rows, a tall monitor) has not changed — a new observer
     * reports where it stands at once, so the list keeps growing until the
     * marker is properly out of sight.
     */
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) setLimit((n) => Math.min(total, n + step));
      },
      // Measured against whatever actually scrolls (the app scrolls `#main`, a sheet scrolls itself): a margin
      // is only honoured around the observer's own root, so against the window "nearly on screen" never came.
      { root: scrollParent(marker), rootMargin: '900px' },
    );
    io.observe(marker);
    return () => io.disconnect();
  }, [marker, limit, total, step]);
  return [Math.min(limit, total), setMarker];
}

/** The nearest ancestor that scrolls up and down, or null for the page itself. */
function scrollParent(el: Element): Element | null {
  for (let p = el.parentElement; p && p !== document.body; p = p.parentElement) {
    const flow = getComputedStyle(p).overflowY;
    if (flow === 'auto' || flow === 'scroll') return p;
  }
  return null;
}

export function usePrevious<T>(value: T): T | undefined {
  const ref = useRef<T | undefined>(undefined);
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

/**
 * Whether an element is on screen, or nearly.
 *
 * For anything whose *content* is expensive — a picture read out of the
 * browser's storage and decoded — so a list of a hundred of them costs only
 * what you can actually see. The ref goes on the element; the boolean turns
 * true once and stays true, because something you have scrolled past should
 * not have to load again if you scroll back.
 */
export function useNearViewport<T extends Element>(margin = '300px'): [(el: T | null) => void, boolean] {
  const [near, setNear] = useState(false);
  const io = useRef<IntersectionObserver | null>(null);
  useEffect(() => () => io.current?.disconnect(), []);
  const ref = useCallback(
    (el: T | null) => {
      io.current?.disconnect();
      if (!el || near) return;
      // No IntersectionObserver (an old browser, a test runner): show everything rather than nothing.
      if (typeof IntersectionObserver === 'undefined') {
        setNear(true);
        return;
      }
      io.current = new IntersectionObserver(
        (entries) => {
          if (!entries.some((e) => e.isIntersecting)) return;
          setNear(true);
          io.current?.disconnect();
        },
        { rootMargin: margin },
      );
      io.current.observe(el);
    },
    [near, margin],
  );
  return [ref, near];
}
